import React, { useId, useState } from 'react';
import { Typography } from 'antd';

import TagSelect from './TagSelect';
import useCollectionTags from '../../hooks/useCollectionTags';
import { useOptionalTags } from '../../contexts/TagsContext';
import { canApplyByHand } from '../../utils/itemTags';

/**
 * A TEMPLATE'S TAGS (docs/plans/tags.md §5.2 "Quick add, multi add, drafts,
 * duplicate — Tag ids", T9): the tags a quick add, or a multi add step, puts
 * on the items it makes. One TagSelect in value mode — the collection's tags
 * as their tree, item tags found by title, and, on a PERSONAL template only,
 * the person's own private tags (a shared template runs for everyone, so the
 * API refuses a private tag there: `allowPrivate`).
 *
 * Only what the person could put on by hand is offered — a restricted tag
 * they don't manage, or a tag a rule applies, is listed greyed with why
 * (utils/itemTags.js canApplyByHand), as the API refuses saving it. Nothing
 * is applied from here: when the template runs, the form shows the tags that
 * land for whoever runs it, before they save, and says what it left off.
 */
export default function TemplateTagsField({
	collectionId,
	collection = null,
	value = [],
	onChange,
	allowPrivate = false,
	label = 'Tags',
	extra = 'Put on each item it makes. The form shows them before saving.',
	disabled = false,
	id,
}) {
	const [open, setOpen] = useState(false);
	const [touched, setTouched] = useState(false);
	// The vocabulary is asked for once the template holds tags or the picker opens.
	const wanted = open || touched || (Array.isArray(value) && value.length > 0);
	const { tags: vocabulary } = useCollectionTags(collectionId || null, { enabled: Boolean(collectionId) && wanted });
	const privateTags = useOptionalTags()?.tags || null;

	const disabledFor = (tag, kind) => {
		if (kind !== 'collection') return null;
		const verdict = canApplyByHand(tag, { canEdit: true, collection });
		return verdict.ok ? null : verdict.reason;
	};

	const fallbackId = useId().replace(/:/g, '');
	const labelId = `${id || `template-tags-${fallbackId}`}-label`;
	return (
		<div className="template-tags-field" data-testid="template-tags-field">
			<label className="d-block" id={labelId}>{label}</label>
			<TagSelect
				multiple
				value={Array.isArray(value) ? value : []}
				onChange={next => { setTouched(true); if (typeof onChange === 'function') onChange(Array.isArray(next) ? next : []); }}
				collectionTags={vocabulary || []}
				privateTags={allowPrivate ? privateTags : null}
				itemTags
				searchCollectionId={collectionId || null}
				disabledFor={disabledFor}
				placeholder="Add tags"
				aria-label={label}
				onOpenChange={next => { if (next) setOpen(true); }}
				disabled={disabled}
				className="template-tags-field__select"
			/>
			{extra ? <Typography.Text type="secondary" className="template-tags-field__hint d-block">{extra}</Typography.Text> : null}
		</div>
	);
}
